import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  ChevronLeft,
  MapPin,
  Loader2,
  CheckCircle2,
  Calendar,
  Clock,
  DollarSign,
  Hammer,
  Paintbrush,
  Home,
  LayoutGrid,
  Bath,
  Wind,
  Sun,
  Box,
  Layers,
  Droplet,
  CheckSquare,
  HelpCircle,
  DoorOpen,
  Square,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function EstimateFlow({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [step, setStep] = useState(1);
  const [serviceBranch, setServiceBranch] = useState<string | null>(null);

  // Shared state
  const [address, setAddress] = useState("");
  const [timeline, setTimeline] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeCommunications, setAgreeCommunications] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);

  // Form activity & abandonment tracking refs
  const maxStepReachedRef = useRef(1);
  const formCompletedRef = useRef(false);
  const formOpenedRef = useRef(false);
  const sessionTrackingIdRef = useRef(crypto.randomUUID());
  const stepStartTimeRef = useRef(Date.now());

  const lastActionTime = useRef(0);

  // Tracking helper — sends activity events to the same backend endpoint
  const trackFormEvent = useCallback(
    (eventType: string, additionalData: Record<string, any> = {}) => {
      const payload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "Estimate Flow - Activity",
        eventType,
        formData: additionalData,
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_b6fe2a5ed7624e6c890769a2b0a10006",
        locationId: "4lOsVXfRzB21f2Hlg8pa",
        sessionId: sessionTrackingIdRef.current,
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
        },
      };
      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", version: "2021-07-28" },
        body: JSON.stringify(payload),
      }).catch(() => {});
    },
    [],
  );

  // Branch A - Roofing
  const [buildingType, setBuildingType] = useState<string | null>(null);
  const [steepness, setSteepness] = useState<string | null>(null);
  const [currentRoof, setCurrentRoof] = useState<string | null>(null);
  const [desiredRoof, setDesiredRoof] = useState<string | null>(null);

  // Branch B - Kitchen
  const [kitchenProject, setKitchenProject] = useState<string | null>(null);
  const [kitchenSize, setKitchenSize] = useState<string | null>(null);
  const [kitchenBudget, setKitchenBudget] = useState<string | null>(null);

  // Branch C - Bathroom
  const [bathroomProject, setBathroomProject] = useState<string | null>(null);
  const [bathroomCount, setBathroomCount] = useState<string | null>(null);
  const [bathroomBudget, setBathroomBudget] = useState<string | null>(null);

  // Branch D - Flooring
  const [flooringType, setFlooringType] = useState<string | null>(null);
  const [flooringRooms, setFlooringRooms] = useState<string[]>([]);
  const [flooringSize, setFlooringSize] = useState<string | null>(null);

  // Branch E - Windows
  const [windowProject, setWindowProject] = useState<string | null>(null);
  const [windowCount, setWindowCount] = useState<string | null>(null);
  const [windowPurpose, setWindowPurpose] = useState<string | null>(null);

  // Branch F - Porches
  const [porchProject, setPorchProject] = useState<string | null>(null);
  const [porchSize, setPorchSize] = useState<string | null>(null);
  const [porchMaterial, setPorchMaterial] = useState<string | null>(null);

  // Branch G - Sunrooms
  const [sunroomType, setSunroomType] = useState<string | null>(null);
  const [sunroomSize, setSunroomSize] = useState<string | null>(null);

  // Branch H - Cabinets
  const [cabinetLocation, setCabinetLocation] = useState<string | null>(null);
  const [cabinetSourcing, setCabinetSourcing] = useState<string | null>(null);

  // Branch I - Painting
  const [paintingAreas, setPaintingAreas] = useState<string[]>([]);
  const [paintingSize, setPaintingSize] = useState<string | null>(null);

  // Branch J - Full Remodel
  const [remodelAreas, setRemodelAreas] = useState<string[]>([]);
  const [remodelBudget, setRemodelBudget] = useState<string | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      // Track form open
      if (!formOpenedRef.current) {
        formOpenedRef.current = true;
        stepStartTimeRef.current = Date.now();
        trackFormEvent("form_opened", {
          url: window.location.href,
          path: window.location.pathname,
        });
      }
    } else {
      // Track abandonment if form was opened but not completed
      if (formOpenedRef.current && !formCompletedRef.current) {
        trackFormEvent("form_abandoned", {
          lastStepReached: maxStepReachedRef.current,
          serviceBranch,
          url: window.location.href,
        });
      }
      const timer = setTimeout(() => {
        setStep(1);
        setServiceBranch(null);
        setAddress("");
        setTimeline(null);
        setName("");
        setEmail("");
        setPhone("");

        setAgreeTerms(false);
        setAgreeCommunications(false);
        setAdditionalInfo("");
        setIsLocating(false);
        setHasRequestedLocation(false);
        setBuildingType(null);
        setSteepness(null);
        setCurrentRoof(null);
        setDesiredRoof(null);
        setKitchenProject(null);
        setKitchenSize(null);
        setKitchenBudget(null);
        setBathroomProject(null);
        setBathroomCount(null);
        setBathroomBudget(null);
        setFlooringType(null);
        setFlooringRooms([]);
        setFlooringSize(null);
        setWindowProject(null);
        setWindowCount(null);
        setWindowPurpose(null);
        setPorchProject(null);
        setPorchSize(null);
        setPorchMaterial(null);
        setSunroomType(null);
        setSunroomSize(null);
        setCabinetLocation(null);
        setCabinetSourcing(null);
        setPaintingAreas([]);
        setPaintingSize(null);
        setRemodelAreas([]);
        setRemodelBudget(null);
        // Reset tracking refs for next session
        maxStepReachedRef.current = 1;
        formCompletedRef.current = false;
        formOpenedRef.current = false;
        sessionTrackingIdRef.current = crypto.randomUUID();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serviceBranch]);

  useEffect(() => {
    const handleOpen = (e: Event) => {
      onOpenChange(true);
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        if (customEvent.detail.name) setName(customEvent.detail.name);
        if (customEvent.detail.email) setEmail(customEvent.detail.email);
      }
    };
    window.addEventListener("open-estimate", handleOpen);
    return () => window.removeEventListener("open-estimate", handleOpen);
  }, [onOpenChange]);

  useEffect(() => {
    if (step === 3 && !address && !hasRequestedLocation) {
      setHasRequestedLocation(true);
      if (navigator.geolocation) {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
              );
              const data = await response.json();
              if (data && data.address) {
                const { house_number, road, city, town, village, state } =
                  data.address;
                const street = [house_number, road].filter(Boolean).join(" ");
                const locality = city || town || village;
                const formattedAddress = [street, locality, state]
                  .filter(Boolean)
                  .join(", ");
                setAddress(formattedAddress || data.display_name);
                toast({
                  title: "Location found",
                  description: "We've autofilled your address.",
                });
              } else if (data && data.display_name) {
                setAddress(data.display_name);
              }
            } catch (error) {
              console.error("Error fetching address:", error);
            } finally {
              setIsLocating(false);
            }
          },
          (error) => {
            console.error("Error getting location:", error);
            setIsLocating(false);
            // Silently fail if location is denied to avoid showing an error to the user
          },
        );
      } else {
        // Silently fail if location is unavailable
      }
    }
  }, [step, address, hasRequestedLocation, toast]);

  // Track step progression and time-on-step
  useEffect(() => {
    if (!isOpen || !formOpenedRef.current) return;
    if (step > maxStepReachedRef.current) {
      maxStepReachedRef.current = step;
    }
    const now = Date.now();
    const timeOnPreviousStep = now - stepStartTimeRef.current;
    stepStartTimeRef.current = now;
    trackFormEvent("step_reached", {
      step,
      serviceBranch,
      maxStepReached: maxStepReachedRef.current,
      timeOnPreviousStepMs: timeOnPreviousStep,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isOpen]);

  const handleNext = () => {
    const now = Date.now();
    if (now - lastActionTime.current < 400) return;
    lastActionTime.current = now;
    setStep((s) => {
      let nextStep = s + 1;
      if (
        serviceBranch &&
        [
          "gutters",
          "gutter-guards",
          "concrete",
          "shingles",
          "tree-removal",
        ].includes(serviceBranch) &&
        nextStep === 2
      )
        nextStep = 3;
      if (
        (serviceBranch === "roofing" || serviceBranch === "roof-replacement") &&
        nextStep === 8
      )
        nextStep = 9;
      if (serviceBranch === "roof-replacement" && nextStep === 8) nextStep = 9;
      if (
        serviceBranch !== "roofing" &&
        serviceBranch !== "roof-replacement" &&
        nextStep === 5
      )
        nextStep = 6;
      return serviceBranch === "roofing" || serviceBranch === "roof-replacement"
        ? Math.min(nextStep, 10)
        : Math.min(nextStep, 7);
    });
  };

  const handleBack = () => {
    const now = Date.now();
    if (now - lastActionTime.current < 400) return;
    lastActionTime.current = now;
    setStep((s) => {
      let prevStep = s - 1;
      if (
        serviceBranch &&
        [
          "gutters",
          "gutter-guards",
          "concrete",
          "shingles",
          "tree-removal",
        ].includes(serviceBranch) &&
        prevStep === 2
      )
        prevStep = 1;
      if (
        (serviceBranch === "roofing" || serviceBranch === "roof-replacement") &&
        prevStep === 8
      )
        prevStep = 7;
      if (serviceBranch === "roof-replacement" && prevStep === 8) prevStep = 7;
      if (
        serviceBranch !== "roofing" &&
        serviceBranch !== "roof-replacement" &&
        prevStep === 5
      )
        prevStep = 4;
      return Math.max(prevStep, 1);
    });
  };

  const handleSubmitForm = () => {
    formCompletedRef.current = true;
    trackFormEvent("form_completed", {
      serviceBranch,
      stepsCompleted: maxStepReachedRef.current,
    });
    const [firstName, ...lastNameParts] = name.split(" ");
    const lastName = lastNameParts.join(" ");

    let branchNotes = "";
    if (serviceBranch === "roofing" || serviceBranch === "roof-replacement") {
      branchNotes = `Building: ${buildingType}, Steepness: ${steepness}, Current Roof: ${currentRoof}, Desired Roof: ${desiredRoof}`;
    } else if (serviceBranch === "kitchen") {
      branchNotes = `Project: ${kitchenProject}, Size: ${kitchenSize}, Budget: ${kitchenBudget}`;
    } else if (serviceBranch === "bathroom") {
      branchNotes = `Project: ${bathroomProject}, Count: ${bathroomCount}, Budget: ${bathroomBudget}`;
    } else if (serviceBranch === "flooring") {
      branchNotes = `Type: ${flooringType}, Rooms: ${flooringRooms.join(", ")}, Size: ${flooringSize}`;
    } else if (serviceBranch === "windows") {
      branchNotes = `Project: ${windowProject}, Count: ${windowCount}, Purpose: ${windowPurpose}`;
    } else if (serviceBranch === "porches") {
      branchNotes = `Project: ${porchProject}, Size: ${porchSize}, Material: ${porchMaterial}`;
    } else if (serviceBranch === "sunrooms") {
      branchNotes = `Type: ${sunroomType}, Size: ${sunroomSize}`;
    } else if (serviceBranch === "cabinets") {
      branchNotes = `Location: ${cabinetLocation}, Sourcing: ${cabinetSourcing}`;
    } else if (serviceBranch === "painting") {
      branchNotes = `Areas: ${paintingAreas.join(", ")}, Size: ${paintingSize}`;
    } else if (serviceBranch === "remodel") {
      branchNotes = `Areas: ${remodelAreas.join(", ")}, Budget: ${remodelBudget}`;
    }

    const notes = [
      `Service: ${serviceBranch}`,
      `Address: ${address}`,
      branchNotes,
      `Additional Info: ${additionalInfo}`,
    ]
      .filter(Boolean)
      .join("\n");

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Estimate Flow",
      formData: {
        first_name: firstName,
        last_name: lastName,
        email: email,

        address1: address,
        calendar_notes: notes,
        "contact.project_type": serviceBranch,
        "contact.timeline": timeline,
        "contact.contact_source": "Estimate Flow Tool",
        "contact.contact_type": "lead",
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",

        address1: "Address",
        calendar_notes: "Project Details",
        "contact.project_type": "Project Type",
        "contact.timeline": "Timeline",
        "contact.contact_source": "Contact Source",
        "contact.contact_type": "Contact Type",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_b6fe2a5ed7624e6c890769a2b0a10006",
      locationId: "4lOsVXfRzB21f2Hlg8pa",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    if (serviceBranch === "roofing" || serviceBranch === "roof-replacement") {
      setStep(10);
    } else {
      setStep(7);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-full sm:max-w-4xl p-0 overflow-hidden bg-background rounded-t-2xl sm:rounded-3xl border-0 shadow-2xl max-h-[85vh] sm:max-h-[90vh]">
        <div className="flex flex-col max-h-[85vh] sm:h-[750px] sm:max-h-[90vh]">
          {/* Header */}
          <div className="px-6 py-4 border-b border-border/40 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {step > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="rounded-full h-8 w-8 bg-secondary/50 hover:bg-secondary"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <span className="text-sm font-semibold text-foreground">
                Step {step} of{" "}
                {serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement"
                  ? 10
                  : 7}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {Array.from({
                length:
                  serviceBranch === "roofing" ||
                  serviceBranch === "roof-replacement"
                    ? 10
                    : 7,
              }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 md:w-8 rounded-full transition-colors ${i < step ? "bg-primary" : "bg-secondary"}`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8">
            {step === 1 && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What service are you looking for?
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[
                    {
                      id: "roofing",
                      label: "Roofing",
                      icon: (
                        <Home
                          className="w-10 h-10 mb-4 text-primary"
                          strokeWidth={1.5}
                        />
                      ),
                    },
                    {
                      id: "roof-replacement",
                      label: "Roof Replacement",
                      icon: (
                        <Home
                          className="w-10 h-10 mb-4 text-primary"
                          strokeWidth={1.5}
                        />
                      ),
                    },
                    {
                      id: "tree-removal",
                      label: "Tree Removal",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M12 22v-8" />
                          <path d="M12 14a4 4 0 0 0-4-4 4 4 0 0 1-4-4 4 4 0 0 1 8 0 4 4 0 0 1 8 0 4 4 0 0 1-4 4 4 4 0 0 0-4 4z" />
                        </svg>
                      ),
                    },
                    {
                      id: "kitchen",
                      label: "Kitchen Remodeling",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="3" y1="10" x2="21" y2="10" />
                          <circle cx="8" cy="15" r="2" />
                          <circle cx="16" cy="15" r="2" />
                        </svg>
                      ),
                    },
                    {
                      id: "bathroom",
                      label: "Bathroom Remodeling",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M14 10V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v7" />
                          <path d="M6 10h8a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2z" />
                          <path d="M8 18v4" />
                          <path d="M12 18v4" />
                        </svg>
                      ),
                    },
                    {
                      id: "flooring",
                      label: "Flooring",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                      ),
                    },
                    {
                      id: "gutters",
                      label: "Gutters",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M3 3v18h18V3H3z" />
                          <path d="M3 9h18" />
                          <path d="M3 15h18" />
                        </svg>
                      ),
                    },
                    {
                      id: "gutter-guards",
                      label: "Gutter Guards",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                      ),
                    },
                    {
                      id: "concrete",
                      label: "Concrete",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M2 12h20" />
                          <path d="M12 2v20" />
                          <path d="M4 4h16v16H4z" />
                        </svg>
                      ),
                    },
                    {
                      id: "shingles",
                      label: "Shingles",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M2 12l10-10 10 10" />
                          <path d="M12 2l10 10-10 10L2 12z" />
                        </svg>
                      ),
                    },
                    {
                      id: "windows",
                      label: "Windows & Doors",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="3" y1="12" x2="21" y2="12"></line>
                          <line x1="12" y1="3" x2="12" y2="21"></line>
                        </svg>
                      ),
                    },
                    {
                      id: "porches",
                      label: "Porches & Decks",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <path d="M2 18h20" />
                          <path d="M4 18v-6" />
                          <path d="M8 18v-6" />
                          <path d="M12 18v-6" />
                          <path d="M16 18v-6" />
                          <path d="M20 18v-6" />
                          <path d="M2 12h20" />
                        </svg>
                      ),
                    },
                    {
                      id: "sunrooms",
                      label: "Sunrooms",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <circle cx="12" cy="12" r="5"></circle>
                          <line x1="12" y1="1" x2="12" y2="3"></line>
                          <line x1="12" y1="21" x2="12" y2="23"></line>
                          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                          <line
                            x1="18.36"
                            y1="18.36"
                            x2="19.78"
                            y2="19.78"
                          ></line>
                          <line x1="1" y1="12" x2="3" y2="12"></line>
                          <line x1="21" y1="12" x2="23" y2="12"></line>
                          <line
                            x1="4.22"
                            y1="19.78"
                            x2="5.64"
                            y2="18.36"
                          ></line>
                          <line
                            x1="18.36"
                            y1="5.64"
                            x2="19.78"
                            y2="4.22"
                          ></line>
                        </svg>
                      ),
                    },
                    {
                      id: "cabinets",
                      label: "Cabinet Installation",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <line x1="4" y1="6" x2="20" y2="6"></line>
                          <line x1="4" y1="12" x2="20" y2="12"></line>
                          <line x1="4" y1="18" x2="20" y2="18"></line>
                        </svg>
                      ),
                    },
                    {
                      id: "painting",
                      label: "Painting",
                      icon: (
                        <Paintbrush
                          className="w-10 h-10 mb-4 text-primary"
                          strokeWidth={1.5}
                        />
                      ),
                    },
                    {
                      id: "remodel",
                      label: "Full Home Remodel",
                      icon: (
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="mb-4 text-primary"
                        >
                          <rect
                            x="4"
                            y="2"
                            width="16"
                            height="20"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="9" y1="22" x2="9" y2="22"></line>
                          <line x1="15" y1="22" x2="15" y2="22"></line>
                          <line x1="8" y1="6" x2="16" y2="6"></line>
                          <line x1="8" y1="10" x2="16" y2="10"></line>
                          <line x1="8" y1="14" x2="16" y2="14"></line>
                          <line x1="8" y1="18" x2="16" y2="18"></line>
                        </svg>
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center h-32 ${serviceBranch === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setServiceBranch(item.id);
                        setTimeout(() => {
                          if (
                            [
                              "gutters",
                              "gutter-guards",
                              "concrete",
                              "shingles",
                              "tree-removal",
                            ].includes(item.id)
                          ) {
                            setStep(3);
                          } else {
                            setStep(2);
                          }
                        }, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-sm font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                    What type of building do you have?
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div
                      className={`relative rounded-2xl overflow-hidden cursor-pointer group border-4 transition-all ${buildingType === "residential" ? "border-primary" : "border-transparent hover:border-primary/50"}`}
                      onClick={() => {
                        setBuildingType("residential");
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <img
                        src="https://vibe.filesafe.space/1781836485235618884/attachments/0aea9c4c-2c1e-4fbe-9383-ddc4afaa6b07.png"
                        alt="Residential Home Remodeling Project in NC"
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-6">
                        <h3 className="text-white text-2xl font-bold flex items-center gap-2">
                          Residential{" "}
                          <ArrowRight className="h-5 w-5 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </h3>
                      </div>
                    </div>
                    <div
                      className={`relative rounded-2xl overflow-hidden cursor-pointer group border-4 transition-all ${buildingType === "commercial" ? "border-primary" : "border-transparent hover:border-primary/50"}`}
                      onClick={() => {
                        setBuildingType("commercial");
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <img
                        src="https://vibe.filesafe.space/1781836485235618884/attachments/24e69fdf-e012-421a-9cba-6816cf6064d5.png"
                        alt="Commercial Property Remodeling Project in NC"
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-6">
                        <h3 className="text-white text-2xl font-bold flex items-center gap-2">
                          Commercial{" "}
                          <ArrowRight className="h-5 w-5 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {step === 2 && serviceBranch === "kitchen" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What type of kitchen project is this?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: "full",
                      label: "Full Remodel",
                      icon: <Home className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "refresh",
                      label: "Cabinet Refresh",
                      icon: <Box className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "countertops",
                      label: "Countertops Only",
                      icon: <Layers className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "layout",
                      label: "Layout Change",
                      icon: (
                        <LayoutGrid className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                    {
                      id: "notsure",
                      label: "Not sure",
                      icon: (
                        <HelpCircle className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-4 md:p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${kitchenProject === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setKitchenProject(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "bathroom" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What type of bathroom project is this?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: "full",
                      label: "Full Remodel",
                      icon: <Bath className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "shower",
                      label: "Tile & Shower Only",
                      icon: <Droplet className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "vanity",
                      label: "Vanity & Fixtures",
                      icon: <Box className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "accessibility",
                      label: "Accessibility Upgrade",
                      icon: (
                        <CheckSquare className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                    {
                      id: "notsure",
                      label: "Not sure",
                      icon: (
                        <HelpCircle className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${bathroomProject === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setBathroomProject(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "flooring" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What type of flooring are you interested in?
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    {
                      id: "vinyl",
                      label: "Vinyl",
                      img: "https://vibe.filesafe.space/1781836485235618884/assets/9217c7e9-45c2-4750-af3a-3e19d6afc731.png",
                    },
                    {
                      id: "lvp",
                      label: "LVP",
                      img: "https://vibe.filesafe.space/1781836485235618884/attachments/f34422a6-cecd-4032-bd26-07d978e76418.png",
                    },
                    {
                      id: "tile",
                      label: "Tile",
                      img: "https://vibe.filesafe.space/1781836485235618884/attachments/79de0427-74db-4d32-b352-95a7a9d422d2.png",
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`relative rounded-2xl overflow-hidden cursor-pointer group aspect-square ${flooringType === item.id ? "ring-2 ring-primary ring-offset-2" : ""}`}
                      onClick={() => {
                        setFlooringType(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <img
                        src={item.img}
                        alt={`${item.label} Service Estimate - Long Vista Remodeling`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-4">
                        <h3 className="text-white text-lg font-bold flex items-center gap-1">
                          {item.label} <ArrowRight className="h-4 w-4" />
                        </h3>
                      </div>
                    </div>
                  ))}
                  <div
                    className={`border rounded-2xl p-4 cursor-pointer transition-all hover:bg-secondary/50 flex flex-col items-center justify-center text-center aspect-square bg-secondary/20 ${flooringType === "notsure" ? "border-primary ring-1 ring-primary" : "border-border/60"}`}
                    onClick={() => {
                      setFlooringType("notsure");
                      setTimeout(handleNext, 300);
                    }}
                  >
                    <h3 className="text-lg font-bold">Not sure</h3>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "windows" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What do you need installed?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      id: "windows",
                      label: "Windows Only",
                      icon: <Square className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "doors",
                      label: "Doors Only",
                      icon: <DoorOpen className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "both",
                      label: "Both",
                      icon: (
                        <LayoutGrid className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${windowProject === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setWindowProject(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "porches" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What are you looking to build?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: "new",
                      label: "New Deck",
                      icon: <Layers className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "repair",
                      label: "Deck Repair",
                      icon: <Hammer className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "covered",
                      label: "Covered Porch",
                      icon: <Home className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "screened",
                      label: "Screened Porch",
                      icon: (
                        <LayoutGrid className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                    {
                      id: "notsure",
                      label: "Not sure",
                      icon: (
                        <HelpCircle className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${porchProject === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setPorchProject(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "sunrooms" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What type of sunroom?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: "three",
                      label: "Three-Season Room",
                      icon: <Sun className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "four",
                      label: "Four-Season Room",
                      icon: <Calendar className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "screen",
                      label: "Screen Enclosure",
                      icon: (
                        <LayoutGrid className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                    {
                      id: "notsure",
                      label: "Not sure",
                      icon: (
                        <HelpCircle className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${sunroomType === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setSunroomType(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "cabinets" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  Where are the cabinets going?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      id: "kitchen",
                      label: "Kitchen",
                      icon: <Home className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "bathroom",
                      label: "Bathroom",
                      icon: <Bath className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "laundry",
                      label: "Laundry Room",
                      icon: <Droplet className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "garage",
                      label: "Garage",
                      icon: <Box className="w-8 h-8 mb-3 text-primary" />,
                    },
                    {
                      id: "other",
                      label: "Other",
                      icon: (
                        <HelpCircle className="w-8 h-8 mb-3 text-primary" />
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${cabinetLocation === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setCabinetLocation(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      {item.icon}
                      <h3 className="text-lg font-bold">{item.label}</h3>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && serviceBranch === "painting" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  What needs to be painted?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    {
                      id: "interior",
                      label: "Interior Walls",
                      icon: <Home className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "exterior",
                      label: "Exterior Siding",
                      icon: <LayoutGrid className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "trim",
                      label: "Trim & Ceilings",
                      icon: <Layers className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "cabinets",
                      label: "Cabinets",
                      icon: <Box className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "fence",
                      label: "Fence or Deck",
                      icon: <Square className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "full",
                      label: "Full Home",
                      icon: <Paintbrush className="w-5 h-5 text-primary" />,
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex items-center gap-3 text-left ${paintingAreas.includes(item.id) ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setPaintingAreas((prev) =>
                          prev.includes(item.id)
                            ? prev.filter((a) => a !== item.id)
                            : [...prev, item.id],
                        );
                      }}
                    >
                      <Checkbox
                        checked={paintingAreas.includes(item.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex items-center gap-3 ml-2">
                        {item.icon}
                        <h3 className="text-lg font-bold">{item.label}</h3>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleNext}
                  disabled={paintingAreas.length === 0}
                  className="w-full md:w-auto md:px-12 bg-primary hover:bg-primary/90 text-white"
                >
                  Continue
                </Button>
              </div>
            )}

            {step === 2 && serviceBranch === "remodel" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8 text-center md:text-left">
                  Which areas are included?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    {
                      id: "kitchen",
                      label: "Kitchen",
                      icon: <Home className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "bathroom",
                      label: "Bathrooms",
                      icon: <Bath className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "flooring",
                      label: "Flooring",
                      icon: <LayoutGrid className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "roof",
                      label: "Roof",
                      icon: <Home className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "windows",
                      label: "Windows & Doors",
                      icon: <Square className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "painting",
                      label: "Painting",
                      icon: <Paintbrush className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "decks",
                      label: "Decks & Porches",
                      icon: <Layers className="w-5 h-5 text-primary" />,
                    },
                    {
                      id: "all",
                      label: "All of the above",
                      icon: <CheckSquare className="w-5 h-5 text-primary" />,
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-xl p-6 cursor-pointer transition-all hover:shadow-md flex items-center gap-3 text-left ${remodelAreas.includes(item.id) ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        if (item.id === "all") {
                          setRemodelAreas(["all"]);
                        } else {
                          setRemodelAreas((prev) => {
                            const newArr = prev.filter((a) => a !== "all");
                            return newArr.includes(item.id)
                              ? newArr.filter((a) => a !== item.id)
                              : [...newArr, item.id];
                          });
                        }
                      }}
                    >
                      <Checkbox
                        checked={remodelAreas.includes(item.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex items-center gap-3 ml-2">
                        {item.icon}
                        <h3 className="text-lg font-bold">{item.label}</h3>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleNext}
                  disabled={remodelAreas.length === 0}
                  className="w-full md:w-auto md:px-12 bg-primary hover:bg-primary/90 text-white"
                >
                  Continue
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col -mx-4 sm:-mx-8 -mb-8">
                <div className="px-4 sm:px-8 pb-4 sm:pb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground text-center sm:text-left">
                    What's your address?
                  </h2>
                  <div className="w-full bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.08)] p-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 border border-border/50 relative z-20">
                    <div className="flex items-center flex-1 px-2">
                      <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                      <Input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Enter your street address"
                        className="border-0 focus-visible:ring-0 text-base h-12 bg-transparent shadow-none w-full"
                      />
                    </div>
                    <Button
                      onClick={handleNext}
                      disabled={!address.trim()}
                      className="bg-primary hover:bg-primary/90 text-white rounded-lg px-8 h-12 font-semibold w-full sm:w-auto"
                    >
                      Continue
                    </Button>
                  </div>
                </div>

                <div className="relative flex-1 bg-muted z-10 min-h-[300px]">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    scrolling="no"
                    marginHeight={0}
                    marginWidth={0}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(address || "Raleigh, NC")}&t=k&z=19&ie=UTF8&iwloc=&output=embed`}
                  ></iframe>

                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full text-sm font-medium flex items-center gap-3 shadow-lg whitespace-nowrap">
                    {isLocating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <div className="h-2 w-2 bg-white rounded-full animate-pulse"></div>
                    )}
                    {isLocating
                      ? "Finding location..."
                      : address
                        ? "Location found"
                        : "Finding location..."}
                  </div>
                </div>
              </div>
            )}

            {step === 4 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h2 className="text-3xl font-bold mb-8">
                    How steep is your roof?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {
                        id: "flat",
                        label: "Flat",
                        desc: "Easily walked on",
                        icon: (
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 36L32 28L48 36L32 44L16 36Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 36V48L32 56L48 48V36"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M32 44V56"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="24"
                              cy="32"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="28"
                              cy="34"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="32"
                              cy="36"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="36"
                              cy="34"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="40"
                              cy="32"
                              r="0.5"
                              fill="currentColor"
                            />
                          </svg>
                        ),
                      },
                      {
                        id: "low",
                        label: "Low",
                        desc: "Can be walked on",
                        icon: (
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 36L32 24L48 36L32 44L16 36Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 36V48L32 56L48 48V36"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M32 44V56"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="24"
                              cy="30"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="28"
                              cy="32"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="32"
                              cy="34"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="36"
                              cy="32"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="40"
                              cy="30"
                              r="0.5"
                              fill="currentColor"
                            />
                          </svg>
                        ),
                      },
                      {
                        id: "moderate",
                        label: "Moderate",
                        desc: "Not easily walked on",
                        icon: (
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 36L32 16L48 36L32 44L16 36Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 36V48L32 56L48 48V36"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M32 44V56"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="24"
                              cy="26"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="28"
                              cy="28"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="32"
                              cy="30"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="36"
                              cy="28"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="40"
                              cy="26"
                              r="0.5"
                              fill="currentColor"
                            />
                          </svg>
                        ),
                      },
                      {
                        id: "steep",
                        label: "Steep",
                        desc: "Cannot be walked on",
                        icon: (
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 40L32 8L48 40L32 46L16 40Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 40V48L32 56L48 48V40"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M32 46V56"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="24"
                              cy="24"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="28"
                              cy="26"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="32"
                              cy="28"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="36"
                              cy="26"
                              r="0.5"
                              fill="currentColor"
                            />
                            <circle
                              cx="40"
                              cy="24"
                              r="0.5"
                              fill="currentColor"
                            />
                          </svg>
                        ),
                      },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className={`border rounded-2xl p-6 cursor-pointer transition-all hover:shadow-md flex flex-col items-center justify-center text-center ${steepness === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                        onClick={() => {
                          setSteepness(item.id);
                          setTimeout(handleNext, 300);
                        }}
                      >
                        <div className="h-16 w-16 mb-6 flex items-center justify-center text-foreground/80">
                          {item.icon}
                        </div>
                        <h3 className="text-xl font-bold mb-2">{item.label}</h3>
                        <p className="text-sm text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {step === 5 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h2 className="text-3xl font-bold mb-8">
                    What is currently on your roof?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      {
                        id: "asphalt",
                        label: "Asphalt",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/fbeaefdb-a00b-487c-bb3f-183732db680d.png",
                      },
                      {
                        id: "metal",
                        label: "Metal",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/ff2811c3-c7fc-4564-ac37-f4b0ab523b21.png",
                      },
                      {
                        id: "tile",
                        label: "Tile",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/9966847e-7d4f-44b3-a224-487a51993dd6.png",
                      },
                      {
                        id: "cedar",
                        label: "Cedar",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/7bd1c41a-876d-4bbb-b6ff-3e095bac4173.png",
                      },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className={`relative rounded-2xl overflow-hidden cursor-pointer group aspect-square ${currentRoof === item.id ? "ring-2 ring-primary ring-offset-2" : ""}`}
                        onClick={() => {
                          setCurrentRoof(item.id);
                          setTimeout(handleNext, 300);
                        }}
                      >
                        <img
                          src={item.img}
                          alt={`${item.label} Flooring Estimate - Long Vista Remodeling`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-4">
                          <h3 className="text-white text-lg font-bold flex items-center gap-1">
                            {item.label} <ArrowRight className="h-4 w-4" />
                          </h3>
                        </div>
                      </div>
                    ))}
                    <div
                      className={`border rounded-2xl p-4 cursor-pointer transition-all hover:bg-secondary/50 flex flex-col items-center justify-center text-center aspect-square bg-secondary/20 ${currentRoof === "notsure" ? "border-primary ring-1 ring-primary" : "border-border/60"}`}
                      onClick={() => {
                        setCurrentRoof("notsure");
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <div className="mb-4 text-muted-foreground">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                          <polyline points="2 12 12 17 22 12"></polyline>
                          <polyline points="2 17 12 22 22 17"></polyline>
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold flex items-center gap-1">
                        Not sure <ArrowRight className="h-4 w-4" />
                      </h3>
                    </div>
                  </div>
                </div>
              )}

            {step === 6 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h2 className="text-3xl font-bold mb-8">
                    What type of roof would you like?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {
                        id: "asphalt",
                        label: "Asphalt",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/fbeaefdb-a00b-487c-bb3f-183732db680d.png",
                      },
                      {
                        id: "metal",
                        label: "Metal",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/ff2811c3-c7fc-4564-ac37-f4b0ab523b21.png",
                      },
                      {
                        id: "tile",
                        label: "Tile",
                        img: "https://vibe.filesafe.space/1781836485235618884/assets/9966847e-7d4f-44b3-a224-487a51993dd6.png",
                      },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className={`relative rounded-2xl overflow-hidden cursor-pointer group aspect-[4/3] md:aspect-square ${desiredRoof === item.id ? "ring-2 ring-primary ring-offset-2" : ""}`}
                        onClick={() => {
                          setDesiredRoof(item.id);
                          setTimeout(handleNext, 300);
                        }}
                      >
                        <img
                          src={item.img}
                          alt={`${item.label} Exterior Painting Estimate - Long Vista Remodeling`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-4">
                          <h3 className="text-white text-lg font-bold flex items-center gap-1">
                            {item.label} <ArrowRight className="h-4 w-4" />
                          </h3>
                        </div>
                      </div>
                    ))}
                    <div
                      className={`border rounded-2xl p-4 cursor-pointer transition-all hover:bg-secondary/50 flex flex-col items-center justify-center text-center aspect-[4/3] md:aspect-square bg-secondary/20 ${desiredRoof === "notsure" ? "border-primary ring-1 ring-primary" : "border-border/60"}`}
                      onClick={() => {
                        setDesiredRoof("notsure");
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <div className="mb-4 text-muted-foreground">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                          <polyline points="2 12 12 17 22 12"></polyline>
                          <polyline points="2 17 12 22 22 17"></polyline>
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold flex items-center gap-1">
                        Not sure <ArrowRight className="h-4 w-4" />
                      </h3>
                    </div>
                  </div>
                </div>
              )}

            {step === 4 && serviceBranch !== "roofing" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h2 className="text-3xl font-bold mb-8">
                  When would you like to start your project?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      id: "notimeline",
                      label: "No timeline",
                      desc: "I do not have a timeline in mind yet.",
                    },
                    {
                      id: "1to3months",
                      label: "In 1-3 months",
                      desc: "Not urgent, but I would like to start soon.",
                    },
                    {
                      id: "now",
                      label: "Now",
                      desc: "I would like to start immediately.",
                    },
                  ].map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-2xl p-5 md:p-6 cursor-pointer transition-all hover:shadow-md text-left flex flex-col justify-center min-h-[100px] md:min-h-[140px] ${timeline === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                      onClick={() => {
                        setTimeline(item.id);
                        setTimeout(handleNext, 300);
                      }}
                    >
                      <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-3">
                        {item.label}
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 6 && serviceBranch !== "roofing" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto w-full">
                <h2 className="text-3xl font-bold mb-8">
                  Where should we send your estimates?
                </h2>

                <div className="space-y-6">
                  <div className="space-y-2 text-left">
                    <label className="text-sm font-medium text-foreground">
                      Name <span className="text-primary">*</span>
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      className="h-12 bg-background"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Email <span className="text-primary">*</span>
                      </label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="john@gmail.com"
                        className="h-12 bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Phone <span className="text-primary">*</span>
                      </label>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(919) 555-0123"
                        className="h-12 bg-background"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 text-left">
                    <label className="text-sm font-medium text-foreground">
                      Additional Info (optional)
                    </label>
                    <textarea
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder="Tell us any specific details about your project..."
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  <div className="space-y-2 text-left">
                    <label className="text-sm font-medium text-foreground">
                      Upload Images (optional)
                    </label>
                    <div className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center text-center hover:bg-secondary/20 transition-colors cursor-pointer relative">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-muted-foreground mb-2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <p className="text-sm font-medium text-foreground">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG, JPG, GIF up to 10MB
                      </p>
                      <input
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        multiple
                        accept="image/*"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 text-left">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="terms-alt"
                        checked={agreeTerms}
                        onCheckedChange={(checked) =>
                          setAgreeTerms(checked as boolean)
                        }
                        className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <div className="space-y-2">
                        <label
                          htmlFor="terms-alt"
                          className="text-sm leading-tight text-foreground cursor-pointer block"
                        >
                          I agree to{" "}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary hover:underline"
                          >
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary hover:underline"
                          >
                            Privacy Policy
                          </a>
                          .<span className="text-primary">*</span>
                        </label>
                        <p className="text-[11px] leading-relaxed text-muted-foreground bg-gray-50 p-3 rounded-lg border border-gray-100">
                          By submitting, you authorize LONG VISTA REMODELING &
                          EXTERIORS LLC to text/call the number above for
                          promotional messages, possibly using automated means.
                          Msg/data rates apply, msg frequency varies. Consent is
                          not a condition of purchase. Text HELP for help and
                          STOP to unsubscribe.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <Button
                      onClick={handleSubmitForm}
                      disabled={!name || !email || !phone || !agreeTerms}
                      className="w-full md:w-auto md:px-12 bg-primary hover:bg-primary/90 text-white h-12 rounded-full text-lg font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                    >
                      Get my personalized estimate
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {step === 7 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h2 className="text-3xl font-bold mb-8">
                    When would you like to start your project?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        id: "notimeline",
                        label: "No timeline",
                        desc: "I do not have a timeline in mind yet.",
                      },
                      {
                        id: "1to3months",
                        label: "In 1-3 months",
                        desc: "Not urgent, but I would like to start soon.",
                      },
                      {
                        id: "now",
                        label: "Now",
                        desc: "I would like to start immediately.",
                      },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className={`border rounded-2xl p-5 md:p-6 cursor-pointer transition-all hover:shadow-md text-left flex flex-col justify-center min-h-[100px] md:min-h-[140px] ${timeline === item.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border/60 hover:border-border"}`}
                        onClick={() => {
                          setTimeline(item.id);
                          setTimeout(handleNext, 300);
                        }}
                      >
                        <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-3">
                          {item.label}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {step === 9 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement") && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto w-full">
                  <h2 className="text-3xl font-bold mb-8">
                    Where should we send your estimates?
                  </h2>

                  <div className="space-y-6">
                    <div className="space-y-2 text-left">
                      <label className="text-sm font-medium text-foreground">
                        Name <span className="text-primary">*</span>
                      </label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Smith"
                        className="h-12 bg-background"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Email <span className="text-primary">*</span>
                        </label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="john@gmail.com"
                          className="h-12 bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Phone <span className="text-primary">*</span>
                        </label>
                        <Input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="(919) 555-0123"
                          className="h-12 bg-background"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 text-left">
                      <label className="text-sm font-medium text-foreground">
                        Additional Info (optional)
                      </label>
                      <textarea
                        value={additionalInfo}
                        onChange={(e) => setAdditionalInfo(e.target.value)}
                        placeholder="Tell us any specific details about your project..."
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>

                    <div className="space-y-2 text-left">
                      <label className="text-sm font-medium text-foreground">
                        Upload Images (optional)
                      </label>
                      <div className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center text-center hover:bg-secondary/20 transition-colors cursor-pointer relative">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-muted-foreground mb-2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-sm font-medium text-foreground">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG, JPG, GIF up to 10MB
                        </p>
                        <input
                          type="file"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          multiple
                          accept="image/*"
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 text-left">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="terms"
                          checked={agreeTerms}
                          onCheckedChange={(checked) =>
                            setAgreeTerms(checked as boolean)
                          }
                          className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <div className="space-y-2">
                          <label
                            htmlFor="terms"
                            className="text-sm leading-tight text-foreground cursor-pointer block"
                          >
                            I agree to{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline"
                            >
                              Terms of Service
                            </a>{" "}
                            and{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline"
                            >
                              Privacy Policy
                            </a>
                            .<span className="text-primary">*</span>
                          </label>
                          <p className="text-[11px] leading-relaxed text-muted-foreground bg-gray-50 p-3 rounded-lg border border-gray-100">
                            By submitting, you authorize LONG VISTA REMODELING &
                            EXTERIORS LLC to text/call the number above for
                            promotional messages, possibly using automated
                            means. Msg/data rates apply, msg frequency varies.
                            Consent is not a condition of purchase. Text HELP
                            for help and STOP to unsubscribe.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-6">
                      <Button
                        onClick={handleSubmitForm}
                        disabled={!name || !email || !phone || !agreeTerms}
                        className="w-full md:w-auto md:px-12 bg-primary hover:bg-primary/90 text-white h-12 rounded-full text-lg font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                      >
                        Get my personalized estimate
                      </Button>
                    </div>
                  </div>
                </div>
              )}

            {((step === 10 &&
              (serviceBranch === "roofing" ||
                serviceBranch === "roof-replacement")) ||
              (step === 7 &&
                serviceBranch !== "roofing" &&
                serviceBranch !== "roof-replacement")) && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col items-center justify-center h-full text-center">
                <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 className="h-10 w-10 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  You&apos;re all set!
                </h2>
                <p className="text-muted-foreground max-w-md">
                  We&apos;ve received your request and will send you a
                  personalized estimate within 24 hours.
                </p>
                <Button
                  onClick={() => onOpenChange(false)}
                  className="mt-8 bg-primary hover:bg-primary/90 text-white px-8 h-12 rounded-full text-lg"
                >
                  Back to Home
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-secondary/50 text-center text-xs font-medium flex items-center justify-center gap-2">
            <a
              href="https://piedmontwebsites.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-black hover:opacity-80 transition-opacity"
            >
              <span className="bg-[#fff0db] text-black px-2 py-1 rounded text-[10px] font-bold border border-black/10">
                P
              </span>{" "}
              Powered by Piedmont Websites & Automations
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
